import { useState, useEffect, useMemo, useCallback } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import Modal from '@/components/base/Modal';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { useToast } from '@/components/base/Toast';

// ---------- Tipos actualizados ----------
interface Compra {
  id: string;
  fecha: string;
  descripcion: string;
  monto: number;
  total: number;            // para mantener compatibilidad con el backend
  comprobanteUrl?: string;  // enlace al archivo subido
}

interface PagoProveedor {
  id: string;
  fecha: string;
  monto: number;
  concepto: string;
  comprobante?: string;
}

interface Proveedor {
  id: string;
  nombre: string;
  rfc: string;
  telefono: string;
  email: string;
  direccion: string;
  tipo: string;
  contactoNombre: string;
  fechaAlta: string;
  compras: Compra[];
  pagos: PagoProveedor[];
}

interface ProveedorForm {
  nombre: string;
  rfc: string;
  telefono: string;
  email: string;
  direccion: string;
  tipo: string;
  contactoNombre: string;
}

interface CompraForm {
  descripcion: string;
  monto: string;            // solo descripción y monto
}

interface PagoForm {
  monto: string;
  concepto: string;
  comprobante: string;
}

const emptyForm: ProveedorForm = { nombre: '', rfc: '', telefono: '', email: '', direccion: '', tipo: 'Materiales', contactoNombre: '' };
const emptyCompraForm: CompraForm = { descripcion: '', monto: '' };
const emptyPagoForm: PagoForm = { monto: '', concepto: '', comprobante: '' };

const tiposProveedor = ['Materiales', 'Ferretería', 'Acero', 'Acabados', 'Eléctrico', 'Hidrosanitario', 'Maquinaria', 'Concreto', 'Servicios', 'Equipo de seguridad', 'Transporte', 'Otro'];

function formatMonto(monto: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(monto);
}

// ---------- Helpers API ----------
const API_URL = 'https://api.airmex.mx/api/v1';

function getToken() {
  return localStorage.getItem('airmex_token') || sessionStorage.getItem('airmex_token');
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token && { Authorization: `Bearer ${token}` }),
    ...((options.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Error desconocido' }));
    throw new Error(error.detail || 'Error en la petición');
  }
  if (res.status === 204) return null;
  return res.json();
}

export default function ProveedoresPage() {
  const { showToast } = useToast();
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');

  // Modal proveedor
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProveedorForm>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<Proveedor | null>(null);

  // Modal detalle
  const [detailTarget, setDetailTarget] = useState<Proveedor | null>(null);
  const [detailTab, setDetailTab] = useState<'compras' | 'pagos'>('compras');

  // Modal compra
  const [compraModalOpen, setCompraModalOpen] = useState(false);
  const [compraTarget, setCompraTarget] = useState<Proveedor | null>(null);
  const [compraForm, setCompraForm] = useState<CompraForm>(emptyCompraForm);
  const [comprobanteFile, setComprobanteFile] = useState<File | null>(null);   // archivo de compra

  // Modal pago
  const [pagoModalOpen, setPagoModalOpen] = useState(false);
  const [pagoTarget, setPagoTarget] = useState<Proveedor | null>(null);
  const [pagoForm, setPagoForm] = useState<PagoForm>(emptyPagoForm);

  // Confirmaciones de eliminación
  const [deleteCompraTarget, setDeleteCompraTarget] = useState<{ prov: Proveedor; compra: Compra } | null>(null);
  const [deletePagoTarget, setDeletePagoTarget] = useState<{ prov: Proveedor; pago: PagoProveedor } | null>(null);

  // Cargar proveedores desde la API
  const loadProveedores = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchWithAuth('/proveedores/');
      setProveedores(data);
    } catch (err: any) {
      showToast('error', 'Error al cargar proveedores', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProveedores(); }, [loadProveedores]);

  // Filtros
  const tiposUnicos = useMemo(() => {
    const set = new Set(proveedores.map(p => p.tipo));
    return Array.from(set);
  }, [proveedores]);

  const filtered = useMemo(() =>
    proveedores.filter(p => {
      const matchSearch = p.nombre.toLowerCase().includes(search.toLowerCase()) ||
        p.rfc.toLowerCase().includes(search.toLowerCase()) ||
        p.contactoNombre.toLowerCase().includes(search.toLowerCase());
      const matchTipo = filtroTipo === 'todos' || p.tipo === filtroTipo;
      return matchSearch && matchTipo;
    }),
    [proveedores, search, filtroTipo]
  );

  const getTotalCompras = (p: Proveedor): number => p.compras.reduce((sum, c) => sum + (c.total || c.monto), 0);
  const getTotalPagado = (p: Proveedor): number => p.pagos.reduce((sum, pg) => sum + pg.monto, 0);
  const getSaldo = (p: Proveedor): number => getTotalCompras(p) - getTotalPagado(p);

  // ---------- Handlers Proveedor ----------
  const openCreate = () => { setEditingId(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (p: Proveedor) => {
    setEditingId(p.id);
    setForm({
      nombre: p.nombre,
      rfc: p.rfc,
      telefono: p.telefono,
      email: p.email,
      direccion: p.direccion,
      tipo: p.tipo,
      contactoNombre: p.contactoNombre,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return;
    try {
      if (editingId) {
        const updated = await fetchWithAuth(`/proveedores/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(form),
        });
        setProveedores(prev => prev.map(p => p.id === editingId ? updated : p));
        if (detailTarget?.id === editingId) setDetailTarget(updated);
        showToast('success', 'Proveedor actualizado', `${form.nombre} se ha modificado.`);
      } else {
        const nuevo = await fetchWithAuth('/proveedores/', {
          method: 'POST',
          body: JSON.stringify(form),
        });
        setProveedores(prev => [...prev, nuevo]);
        showToast('success', 'Proveedor registrado', `${form.nombre} se ha dado de alta.`);
      }
      setModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
    } catch (err: any) {
      showToast('error', 'Error al guardar', err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await fetchWithAuth(`/proveedores/${deleteTarget.id}`, { method: 'DELETE' });
      setProveedores(prev => prev.filter(p => p.id !== deleteTarget.id));
      if (detailTarget?.id === deleteTarget.id) setDetailTarget(null);
      showToast('success', 'Proveedor eliminado', `${deleteTarget.nombre} se ha eliminado.`);
      setDeleteTarget(null);
    } catch (err: any) {
      showToast('error', 'Error al eliminar', err.message);
    }
  };

  // ---------- Handlers Compras ----------
  const openCompraModal = (p: Proveedor) => {
    setCompraTarget(p);
    setCompraForm(emptyCompraForm);
    setComprobanteFile(null);
    setCompraModalOpen(true);
  };

  const handleCompraSave = async () => {
    if (!compraTarget || !compraForm.descripcion.trim() || !compraForm.monto) return;
    const formData = new FormData();
    formData.append('descripcion', compraForm.descripcion.trim());
    formData.append('monto', compraForm.monto);
    if (comprobanteFile) formData.append('comprobante', comprobanteFile);

    try {
      const updated = await fetchWithAuth(`/proveedores/${compraTarget.id}/compras`, {
        method: 'POST',
        body: formData,
        headers: {},  // el navegador asigna Content-Type multipart
      });
      setProveedores(prev => prev.map(p => p.id === compraTarget.id ? updated : p));
      if (detailTarget?.id === compraTarget.id) setDetailTarget(updated);
      showToast('success', 'Compra registrada', `${compraForm.descripcion} → ${formatMonto(parseFloat(compraForm.monto))}`);
      setCompraModalOpen(false);
      setCompraTarget(null);
      setComprobanteFile(null);
    } catch (err: any) {
      showToast('error', 'Error al registrar compra', err.message);
    }
  };

  const handleDeleteCompra = async () => {
    if (!deleteCompraTarget) return;
    try {
      const updated = await fetchWithAuth(
        `/proveedores/${deleteCompraTarget.prov.id}/compras/${deleteCompraTarget.compra.id}`,
        { method: 'DELETE' }
      );
      setProveedores(prev => prev.map(p => p.id === deleteCompraTarget.prov.id ? updated : p));
      if (detailTarget?.id === deleteCompraTarget.prov.id) setDetailTarget(updated);
      showToast('info', 'Compra eliminada', `"${deleteCompraTarget.compra.descripcion}" eliminada.`);
      setDeleteCompraTarget(null);
    } catch (err: any) {
      showToast('error', 'Error al eliminar compra', err.message);
    }
  };

  // ---------- Handlers Pagos ----------
  const openPagoModal = (p: Proveedor) => {
    setPagoTarget(p);
    const saldo = getSaldo(p);
    setPagoForm({ ...emptyPagoForm, monto: saldo > 0 ? saldo.toString() : '' });
    setPagoModalOpen(true);
  };

  const handlePagoSave = async () => {
    if (!pagoTarget || !pagoForm.monto || !pagoForm.concepto.trim()) return;
    const monto = parseFloat(pagoForm.monto);
    if (isNaN(monto) || monto <= 0) return;

    const payload = {
      monto,
      concepto: pagoForm.concepto,
      comprobante: pagoForm.comprobante || undefined,
    };

    try {
      const updated = await fetchWithAuth(`/proveedores/${pagoTarget.id}/pagos`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setProveedores(prev => prev.map(p => p.id === pagoTarget.id ? updated : p));
      if (detailTarget?.id === pagoTarget.id) setDetailTarget(updated);
      showToast('success', 'Pago registrado', `Se registró ${formatMonto(monto)} a ${pagoTarget.nombre}`);
      setPagoModalOpen(false);
      setPagoTarget(null);
    } catch (err: any) {
      showToast('error', 'Error al registrar pago', err.message);
    }
  };

  const handleDeletePago = async () => {
    if (!deletePagoTarget) return;
    try {
      const updated = await fetchWithAuth(
        `/proveedores/${deletePagoTarget.prov.id}/pagos/${deletePagoTarget.pago.id}`,
        { method: 'DELETE' }
      );
      setProveedores(prev => prev.map(p => p.id === deletePagoTarget.prov.id ? updated : p));
      if (detailTarget?.id === deletePagoTarget.prov.id) setDetailTarget(updated);
      showToast('info', 'Pago eliminado', `Pago de ${formatMonto(deletePagoTarget.pago.monto)} eliminado.`);
      setDeletePagoTarget(null);
    } catch (err: any) {
      showToast('error', 'Error al eliminar pago', err.message);
    }
  };

  // ---------- Vista de carga ----------
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400" />
        </div>
      </DashboardLayout>
    );
  }

  // ---------- UI principal ----------
  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">Catálogo de Proveedores</h1>
            <p className="text-sm text-foreground-500 mt-0.5">{proveedores.length} proveedores registrados</p>
          </div>
          <button onClick={openCreate} className="px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line mr-1.5" />Nuevo proveedor
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar proveedor, RFC o contacto..." className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
          </div>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="max-w-[180px] px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100 cursor-pointer"
          >
            <option value="todos">Todos los tipos</option>
            {tiposUnicos.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        {/* Tabla de proveedores (sin cambios) */}
        <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-store-2-line text-xl text-foreground-300" /></div>
              <p className="text-sm text-foreground-500">No se encontraron proveedores.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/50">
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase px-5 py-3">Proveedor</th>
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase px-5 py-3 hidden lg:table-cell">Tipo</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3">Total comprado</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3 hidden md:table-cell">Pagado</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3">Saldo</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {filtered.map((p) => {
                    const totalComprado = getTotalCompras(p);
                    const totalPagado = getTotalPagado(p);
                    const saldo = totalComprado - totalPagado;

                    return (
                      <tr key={p.id} className="hover:bg-background-50/50 transition-colors">
                        <td className="px-5 py-3 cursor-pointer" onClick={() => setDetailTarget(p)}>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                              <i className="ri-store-2-line text-accent-600 text-sm" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors">{p.nombre}</p>
                              <p className="text-[11px] text-foreground-400">{p.contactoNombre} · {p.telefono}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 hidden lg:table-cell">
                          <span className="text-xs text-foreground-600 bg-background-100 px-2 py-0.5 rounded-full">{p.tipo}</span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="text-sm font-semibold text-foreground-800">{formatMonto(totalComprado)}</span>
                        </td>
                        <td className="px-5 py-3 text-right hidden md:table-cell">
                          <span className="text-sm text-foreground-600">{formatMonto(totalPagado)}</span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className={`inline-flex items-center gap-1 text-sm font-semibold ${saldo > 0 ? 'text-red-600' : saldo < 0 ? 'text-amber-600' : 'text-green-600'}`}>
                            {saldo > 0 && <i className="ri-arrow-up-line text-[10px]" />}
                            {saldo < 0 && <i className="ri-arrow-down-line text-[10px]" />}
                            {saldo === 0 && <i className="ri-check-line text-[10px]" />}
                            {formatMonto(Math.abs(saldo))}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={(e) => { e.stopPropagation(); setDetailTarget(p); setDetailTab('compras'); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-100 text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Ver todas las compras">
                              <i className="ri-file-list-3-line text-sm" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); openCompraModal(p); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-100 text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Registrar compra">
                              <i className="ri-shopping-cart-line text-sm" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); openPagoModal(p); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-green-100 text-foreground-400 hover:text-green-600 transition-colors cursor-pointer" title="Registrar pago">
                              <i className="ri-bank-card-line text-sm" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); openEdit(p); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary-100 text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer" title="Editar proveedor">
                              <i className="ri-edit-line text-sm" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer" title="Eliminar proveedor">
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal crear/editar proveedor (sin cambios) */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? 'Editar proveedor' : 'Nuevo proveedor'} size="lg"
        footer={
          <>
            <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
            <button onClick={handleSave} className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${!form.nombre.trim() ? 'bg-background-200 text-foreground-400 cursor-not-allowed' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`} disabled={!form.nombre.trim()}>{editingId ? 'Guardar cambios' : 'Registrar proveedor'}</button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Razón social *</label>
            <input type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Materiales del Centro S.A." className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">RFC</label>
              <input type="text" value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value })} placeholder="Ej: MCE921015AB3" className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 font-mono placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Tipo de proveedor</label>
              <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100 cursor-pointer">
                {tiposProveedor.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Teléfono</label>
              <input type="text" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="Ej: 5551234501" className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Ej: ventas@proveedor.mx" className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nombre del contacto</label>
            <input type="text" value={form.contactoNombre} onChange={(e) => setForm({ ...form, contactoNombre: e.target.value })} placeholder="Ej: Ing. Ricardo Mendoza" className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Dirección</label>
            <input type="text" value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} placeholder="Ej: Av. Central 2450, Col. Industrial, CDMX" className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
          </div>
        </div>
      </Modal>

      {/* Modal detalle de proveedor – tabla de compras actualizada */}
      <Modal
        open={!!detailTarget}
        onClose={() => { setDetailTarget(null); setDetailTab('compras'); }}
        title={detailTarget?.nombre || ''}
        size="xl"
        footer={
          <button onClick={() => { setDetailTarget(null); setDetailTab('compras'); }} className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cerrar</button>
        }
      >
        {detailTarget && (
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-background-200">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-xs bg-background-100 text-foreground-500 px-2 py-0.5 rounded-full">{detailTarget.tipo}</span>
                  <span className="text-xs text-foreground-400 font-mono">{detailTarget.rfc}</span>
                </div>
                <p className="text-xs text-foreground-500">{detailTarget.contactoNombre} · {detailTarget.telefono} · {detailTarget.email}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="bg-background-50 rounded-lg border border-background-200 p-3 text-center">
                <p className="text-[10px] text-foreground-400 uppercase mb-1">Total comprado</p>
                <p className="text-base font-bold text-foreground-800">{formatMonto(getTotalCompras(detailTarget))}</p>
              </div>
              <div className="bg-background-50 rounded-lg border border-background-200 p-3 text-center">
                <p className="text-[10px] text-foreground-400 uppercase mb-1">Total pagado</p>
                <p className="text-base font-bold text-green-600">{formatMonto(getTotalPagado(detailTarget))}</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${getSaldo(detailTarget) > 0 ? 'bg-red-50 border-red-200' : getSaldo(detailTarget) < 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                <p className={`text-[10px] uppercase mb-1 ${getSaldo(detailTarget) > 0 ? 'text-red-500' : getSaldo(detailTarget) < 0 ? 'text-amber-600' : 'text-green-600'}`}>
                  {getSaldo(detailTarget) > 0 ? 'Saldo pendiente' : getSaldo(detailTarget) < 0 ? 'A favor' : 'Liquidado'}
                </p>
                <p className={`text-base font-bold ${getSaldo(detailTarget) > 0 ? 'text-red-600' : getSaldo(detailTarget) < 0 ? 'text-amber-600' : 'text-green-600'}`}>
                  {formatMonto(Math.abs(getSaldo(detailTarget)))}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="flex rounded-lg border border-background-200 overflow-hidden">
                <button
                  onClick={() => setDetailTab('compras')}
                  className={`px-4 py-2 text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${detailTab === 'compras' ? 'bg-primary-500 text-background-50' : 'bg-background-50 text-foreground-500 hover:bg-background-100'}`}
                >
                  <i className="ri-shopping-cart-line mr-1" />Compras ({detailTarget.compras.length})
                </button>
                <button
                  onClick={() => setDetailTab('pagos')}
                  className={`px-4 py-2 text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${detailTab === 'pagos' ? 'bg-primary-500 text-background-50' : 'bg-background-50 text-foreground-500 hover:bg-background-100'}`}
                >
                  <i className="ri-bank-card-line mr-1" />Pagos ({detailTarget.pagos.length})
                </button>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openCompraModal(detailTarget)} className="px-3 py-1.5 rounded-lg bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line mr-1" />Compra
                </button>
                <button onClick={() => openPagoModal(detailTarget)} className="px-3 py-1.5 rounded-lg bg-green-100 text-green-700 text-xs font-medium hover:bg-green-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line mr-1" />Pago
                </button>
              </div>
            </div>

            {/* Tabla de compras simplificada */}
            {detailTab === 'compras' && (
              <div className="border border-background-200 rounded-lg overflow-hidden max-h-[360px] overflow-y-auto">
                {detailTarget.compras.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-background-100 flex items-center justify-center"><i className="ri-shopping-cart-line text-foreground-300" /></div>
                    <p className="text-xs text-foreground-400">Sin compras registradas</p>
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-background-100/60">
                        <th className="text-left text-[10px] font-medium text-foreground-400 uppercase px-3 py-2">Fecha</th>
                        <th className="text-left text-[10px] font-medium text-foreground-400 uppercase px-3 py-2">Descripción</th>
                        <th className="text-right text-[10px] font-medium text-foreground-400 uppercase px-3 py-2">Monto</th>
                        <th className="text-center text-[10px] font-medium text-foreground-400 uppercase px-3 py-2">Cmp.</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-100">
                      {detailTarget.compras.map((c) => (
                        <tr key={c.id} className="hover:bg-background-50/50">
                          <td className="px-3 py-2 text-foreground-500">{new Date(c.fecha + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                          <td className="px-3 py-2 text-foreground-700">{c.descripcion}</td>
                          <td className="px-3 py-2 text-right font-semibold text-foreground-800">{formatMonto(c.total || c.monto)}</td>
                          <td className="px-3 py-2 text-center">
                            {c.comprobanteUrl ? (
                              <a href={c.comprobanteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-5 h-5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100">
                                <i className="ri-file-line text-[10px]" />
                              </a>
                            ) : (
                              <span className="text-foreground-300">—</span>
                            )}
                          </td>
                          <td className="px-1 py-2">
                            <button onClick={() => setDeleteCompraTarget({ prov: detailTarget, compra: c })} className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-100 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer">
                              <i className="ri-close-line text-[10px]" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Tabla de pagos (sin cambios) */}
            {detailTab === 'pagos' && (
              <div className="border border-background-200 rounded-lg overflow-hidden max-h-[360px] overflow-y-auto">
                {detailTarget.pagos.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-background-100 flex items-center justify-center"><i className="ri-bank-card-line text-foreground-300" /></div>
                    <p className="text-xs text-foreground-400">Sin pagos registrados</p>
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-background-100/60">
                        <th className="text-left text-[10px] font-medium text-foreground-400 uppercase px-3 py-2">Fecha</th>
                        <th className="text-left text-[10px] font-medium text-foreground-400 uppercase px-3 py-2">Concepto</th>
                        <th className="text-right text-[10px] font-medium text-foreground-400 uppercase px-3 py-2">Monto</th>
                        <th className="text-left text-[10px] font-medium text-foreground-400 uppercase px-3 py-2 hidden sm:table-cell">Comprobante</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-100">
                      {detailTarget.pagos.map((pg) => (
                        <tr key={pg.id} className="hover:bg-background-50/50">
                          <td className="px-3 py-2 text-foreground-500">{new Date(pg.fecha + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                          <td className="px-3 py-2 text-foreground-700">{pg.concepto}</td>
                          <td className="px-3 py-2 text-right font-semibold text-green-600">{formatMonto(pg.monto)}</td>
                          <td className="px-3 py-2 text-foreground-400 font-mono hidden sm:table-cell">{pg.comprobante || '—'}</td>
                          <td className="px-1 py-2">
                            <button onClick={() => setDeletePagoTarget({ prov: detailTarget, pago: pg })} className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-100 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer">
                              <i className="ri-close-line text-[10px]" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal registrar compra (simplificado, con archivo) */}
      <Modal
        open={compraModalOpen}
        onClose={() => { setCompraModalOpen(false); setCompraTarget(null); }}
        title={compraTarget ? `Registrar compra — ${compraTarget.nombre}` : 'Registrar compra'}
        size="md"
        footer={
          <>
            <button onClick={() => { setCompraModalOpen(false); setCompraTarget(null); }} className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
              Cancelar
            </button>
            <button onClick={handleCompraSave} disabled={!compraForm.descripcion.trim() || !compraForm.monto}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                !compraForm.descripcion.trim() || !compraForm.monto
                  ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              Registrar compra
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Descripción *</label>
            <input
              type="text"
              value={compraForm.descripcion}
              onChange={(e) => setCompraForm({ ...compraForm, descripcion: e.target.value })}
              placeholder="Ej: Compra de materiales"
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Monto total *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">$</span>
              <input
                type="number"
                value={compraForm.monto}
                onChange={(e) => setCompraForm({ ...compraForm, monto: e.target.value })}
                placeholder="0.00"
                className="w-full pl-7 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Comprobante (opcional)</label>
            <input
              type="file"
              onChange={(e) => setComprobanteFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-foreground-600 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-primary-50 file:text-primary-600 hover:file:bg-primary-100 file:cursor-pointer file:transition-colors"
            />
            {comprobanteFile && (
              <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                <i className="ri-check-line" />{comprobanteFile.name}
              </p>
            )}
          </div>
        </div>
      </Modal>

      {/* Modal registrar pago (sin cambios) */}
      <Modal
        open={pagoModalOpen}
        onClose={() => { setPagoModalOpen(false); setPagoTarget(null); }}
        title={pagoTarget ? `Registrar pago — ${pagoTarget.nombre}` : 'Registrar pago'}
        size="md"
        footer={
          <>
            <button onClick={() => { setPagoModalOpen(false); setPagoTarget(null); }} className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
            <button onClick={handlePagoSave} className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${!pagoForm.monto || !pagoForm.concepto.trim() ? 'bg-background-200 text-foreground-400 cursor-not-allowed' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`} disabled={!pagoForm.monto || !pagoForm.concepto.trim()}>
              Registrar pago
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Monto (MXN) *</label>
            <input type="number" value={pagoForm.monto} onChange={(e) => setPagoForm({ ...pagoForm, monto: e.target.value })} placeholder="Ej: 15000" className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
            {pagoTarget && (
              <p className="text-[11px] text-foreground-400 mt-1">Saldo pendiente: {formatMonto(getSaldo(pagoTarget))}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Concepto *</label>
            <input type="text" value={pagoForm.concepto} onChange={(e) => setPagoForm({ ...pagoForm, concepto: e.target.value })} placeholder="Ej: Pago parcial compras enero 2026" className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Comprobante / Referencia</label>
            <input type="text" value={pagoForm.comprobante} onChange={(e) => setPagoForm({ ...pagoForm, comprobante: e.target.value })} placeholder="Ej: TRF-20260201-001" className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
          </div>
        </div>
      </Modal>

      {/* Confirmaciones de eliminación */}
      <ConfirmDialog
        open={!!deleteCompraTarget}
        onClose={() => setDeleteCompraTarget(null)}
        onConfirm={handleDeleteCompra}
        title="Eliminar compra"
        message={`¿Eliminar "${deleteCompraTarget?.compra.descripcion}" por ${deleteCompraTarget ? formatMonto(deleteCompraTarget.compra.total || deleteCompraTarget.compra.monto) : ''}?`}
        confirmLabel="Sí, eliminar"
        variant="danger"
      />
      <ConfirmDialog
        open={!!deletePagoTarget}
        onClose={() => setDeletePagoTarget(null)}
        onConfirm={handleDeletePago}
        title="Eliminar pago"
        message={`¿Eliminar el pago de ${deletePagoTarget ? formatMonto(deletePagoTarget.pago.monto) : ''}?`}
        confirmLabel="Sí, eliminar"
        variant="danger"
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar proveedor"
        message={`¿Estás seguro de eliminar a "${deleteTarget?.nombre}"? También se eliminará todo su historial de compras y pagos.`}
        confirmLabel="Sí, eliminar"
        variant="danger"
      />
    </DashboardLayout>
  );
}